import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  listLicensesOverview,
  formatDateTime,
  type LicenseOverviewRow,
} from "../api/functions";

/**
 * Read-only overview of every license across all accounts, with the desktop
 * sync telemetry (app version, last report/master sync, and the customer's
 * sync settings) joined server-side. Sourced from `listLicensesOverview` —
 * telemetry columns stay blank until an install's first master/report push.
 */
export function AllLicensesPage() {
  const [rows, setRows] = useState<LicenseOverviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    listLicensesOverview()
      .then((r) => setRows(r.rows))
      .catch((e) => setError(e?.message ?? "Failed to load licenses"));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.restaurantName, r.restaurantId, r.ownerEmail, r.driveEmail ?? "", r.appVersion ?? ""]
        .some((f) => f.toLowerCase().includes(q)),
    );
  }, [rows, query]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">All licenses</h1>
          <p className="text-slate-500 text-sm">
            Every license across all accounts, with last-known desktop sync status.
          </p>
        </div>
        {rows && (
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, ID, email…"
            className="w-64 rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        )}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {rows === null && !error && <div className="text-slate-500">Loading…</div>}

      {filtered && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Restaurant</th>
                <th className="px-4 py-3 font-medium">ID</th>
                <th className="px-4 py-3 font-medium">Owner email</th>
                <th className="px-4 py-3 font-medium" title="Google account the desktop backups are stored in">Drive account</th>
                <th className="px-4 py-3 font-medium">Report sync</th>
                <th className="px-4 py-3 font-medium">Last report sync</th>
                <th className="px-4 py-3 font-medium">Last master sync</th>
                <th className="px-4 py-3 font-medium">App version</th>
                <th className="px-4 py-3 font-medium">Backup path</th>
                <th className="px-4 py-3 font-medium">Sync before day-end</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.docId} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800">
                    <Link
                      to={`/admin/accounts/${r.accountId}`}
                      className="text-indigo-600 hover:underline"
                    >
                      {r.restaurantName || "—"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {r.restaurantId || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.ownerEmail || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {r.driveEmail ?? <span className="text-slate-400" title="Not reported yet — the desktop sends it on its next heartbeat">—</span>}
                  </td>
                  <td className="px-4 py-3"><YesNo on={r.reportSyncEnabled} /></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {formatDateTime(r.lastReportPush)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {formatDateTime(r.lastMasterPush)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                    {r.appVersion || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs max-w-xs truncate" title={r.backupPath ?? ""}>
                    {r.backupPath || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3"><YesNo on={r.requireSyncBeforeDayEnd} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500">
              {query ? "No licenses match your search." : "No licenses yet."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function YesNo({ on }: { on: boolean }) {
  return (
    <span
      className={
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium " +
        (on ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600")
      }
    >
      {on ? "Yes" : "No"}
    </span>
  );
}
